import { useState } from 'react';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { useBulkAssignCoachesMutation } from '@backrs_ui/grow-ui/src/redux/coachingDashboard/coachingDashboard.api';
import type { CoachSummary } from '@backrs_ui/grow-ui/src/redux/coachingDashboard/coachingDashboard.types';
import {
  type NamedRef,
  parseBulkAssignResult,
} from '@backrs_ui/grow-ui/src/redux/coachingDashboard/parseBulkAssignResult';
import { personName } from '@backrs_ui/grow-ui/src/utils/personName';

import { CoachAssignmentModalView } from './CoachAssignmentModalView';

const {
  modules: { coachingDashboard },
} = appStrings;

const EMPTY_COACHES: CoachSummary[] = [];

const formatSkipped = (skippedCoaches: NamedRef[], skippedMembers: NamedRef[]): string => {
  const parts: string[] = [];

  if (skippedCoaches.length > 0) {
    parts.push(`coaches ${skippedCoaches.map((c) => c.name).join(', ')}`);
  }
  if (skippedMembers.length > 0) {
    parts.push(`members ${skippedMembers.map((m) => m.name).join(', ')}`);
  }

  return parts.join('; ');
};

type BulkAssignCoachesModalProps = Readonly<{
  open: boolean;
  onClose: () => void;
  membersById: ReadonlyMap<number, NamedRef>;
  onSuccess?: () => void;
}>;

export const BulkAssignCoachesModal = ({
  open,
  onClose,
  membersById,
  onSuccess,
}: BulkAssignCoachesModalProps) => {
  const [bulkAssignCoaches, { isLoading: isSaving }] = useBulkAssignCoachesMutation();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const memberIds = Array.from(membersById.keys());

  const handleSave = async (selected: CoachSummary[]) => {
    if (memberIds.length === 0) return;

    const coachIds = selected.map((c) => c.id);

    if (coachIds.length === 0) return;

    try {
      const response = await bulkAssignCoaches({
        userIds: memberIds,
        coachIds,
      }).unwrap();
      const coachesById = new Map<number, NamedRef>(
        selected.map((c) => [c.id, { id: c.id, name: personName(c) }]),
      );
      const result = parseBulkAssignResult(response, { coachesById, membersById });

      switch (result.kind) {
        case 'full':
          setSuccessMessage(coachingDashboard.bulkAssignSuccess);
          onSuccess?.();
          onClose();

          return;
        case 'partial':
          setSuccessMessage(
            coachingDashboard.bulkAssignPartialSuccess(
              result.newCount,
              formatSkipped(result.skippedCoaches, result.skippedMembers),
            ),
          );
          onSuccess?.();
          onClose();

          return;
        case 'noop':
          setSuccessMessage(coachingDashboard.bulkAssignNoop);
          onSuccess?.();
          onClose();

          return;
        case 'failed':
          setErrorMessage(
            coachingDashboard.bulkAssignAllSkipped(
              formatSkipped(result.skippedCoaches, result.skippedMembers),
            ),
          );

          return;
      }
    } catch (error) {
      console.error('Failed to assign coaches:', error);
      setErrorMessage(coachingDashboard.bulkAssignError);
    }
  };

  return (
    <CoachAssignmentModalView
      open={open}
      onClose={onClose}
      description={coachingDashboard.bulkAssignDescription(memberIds.length)}
      initialSelectedCoaches={EMPTY_COACHES}
      requireSelection
      isSaving={isSaving}
      errorMessage={errorMessage}
      onCloseError={() => setErrorMessage(null)}
      successMessage={successMessage}
      onCloseSuccess={() => setSuccessMessage(null)}
      onSave={handleSave}
    />
  );
};
